import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import * as Promise from 'bluebird';
import { Component } from 'react';
import {
  ColumnDefinition,
  getFilters,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter,
  getPagesCount,
  isFilterExist,
  isSortExist,
  TABLE_SORT_DIRECTION,
} from 'Src/helpers/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import { Grid } from 'Src/components/Grid/Grid';
import { History, Location } from 'history';
import { SimpleModal } from '../../../../../../../components/SimpleModal/SimpleModal';
import { Customer, CustomerPermission } from '../../../../../../../models/customers';
import { getCustomer } from '../../../../../../../services/superadmin/customers';
import {
  addCustomerPermission,
  deleteCustomerPermission,
  getAllCustomerPermissions,
  getCustomerPermissions,
  getCustomerPermissionsCount,
  updateCustomerPermission,
} from '../../../../../../../services/superadmin/customerPermissions';
import { CustomerPermissionForm } from './CustomerPermissionForm/CustomerPermissionForm';
import * as propz from 'propz';
import { convertToFilterWithPlus } from '../../../../../../../helpers/table';
import { LIMIT } from '../../../../../../../consts/table';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Username',
    field: 'username',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['username'],
  },
  {
    text: 'Password',
    field: 'password',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['password'],
  },
];

interface State {
  items: CustomerPermission[];
  currentPage: number;
  selectedItems: CustomerPermission[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  customer: Customer;
  isConfirmationModalOpen: boolean;
  isCustomerPermissionFormModalOpen: boolean;
  isErrorModalOpen: boolean;
  errorMessage: string;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class CustomerPermissions extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      customer: undefined,
      isConfirmationModalOpen: false,
      isCustomerPermissionFormModalOpen: false,
      isErrorModalOpen: false,
      errorMessage: '',
    };
  }

  getCustomerId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    const customerId = search.customer as string;
    return customerId;
  }

  componentDidMount() {
    this.setState({
      isLoading: true,
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  setAdditionalItems() {
    const { user } = this.props;
    const customerId = this.getCustomerId();

    return getCustomer(user, customerId).then(customer => {
      this.setState({
        customer: customer,
      });

      return true;
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true,
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters(COLUMNS, search);

    const isShowFilter = isFilterExist(filters);
    const isDataFiltered = isFilterExist(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter(page, where, order);

    const customerId = this.getCustomerId();

    const getItemsPromise = getCustomerPermissions(user, customerId, serverQueryFilter);
    const getItemsCountPromise = getCustomerPermissionsCount(user, customerId, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return Promise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection as TABLE_SORT_DIRECTION,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false,
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const customerId = this.getCustomerId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `customer=${customerId}&${search.join('&')}`,
      state: history.location.state,
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const selectedItemsNext = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: selectedItemsNext,
      isSelectAllChecked: true,
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true,
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const customerId = this.getCustomerId();

    getAllCustomerPermissions(user, customerId, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const selectedItemsNext = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: selectedItemsNext,
        isSelectAllChecked: true,
        isLoading: false,
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const customerId = this.getCustomerId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `customer=${customerId}&${search.join('&')}`,
      state: history.location.state,
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue,
            },
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue,
            },
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value,
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue,
        };
      }
    }

    this.setState({
      filters: nextFilters,
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    const filtersConverted = convertToFilterWithPlus(filters);

    if (isFilter) {
      search.push(getSearchFilter(filtersConverted));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    const customerId = this.getCustomerId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `customer=${customerId}&${search.join('&')}`,
      state: history.location.state,
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    const customerId = this.getCustomerId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `customer=${customerId}`,
      state: history.location.state,
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
    });
  };

  goBack = () => {
    const { history, location } = this.props;
    const { state } = location;
    const { search } = state as any;
    const customerId = this.getCustomerId();

    history.push({
      pathname: '/customers',
      search: search,
      state: { customerId: customerId },
    });
  };

  onCloseErrorClick = () => {
    this.setState({
      isErrorModalOpen: false,
      errorMessage: '',
      isLoading: true,
    });
    this.setItems();
  };

  showConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: true,
    });
  };

  hideConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: false,
    });
  };

  renderConfirmationModal(): React.ReactNode {
    const { isConfirmationModalOpen, selectedItems } = this.state;

    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={'Confirmation'}
        body={`Are you sure you want to remove ${selectedItems.length === 1 ? 'permission' : 'permissions'}?`}
        buttonText={'Remove'}
        buttonCancelText={'Cancel'}
        onButtonClick={this.onDeletePermissionsClick}
        onCloseClick={this.hideConfirmationModal}
      />
    );
  }

  renderSendingError(): React.ReactNode {
    const { errorMessage, isErrorModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title={'Error'}
        body={errorMessage}
        buttonCancelText={'Ok'}
        onCloseClick={this.onCloseErrorClick}
      />
    );
  }

  onDeletePermissionsClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
      isConfirmationModalOpen: false,
    });

    const customerId = this.getCustomerId();

    Promise.all(selectedItems.map(selectedItem => deleteCustomerPermission(user, customerId, selectedItem.id))).then(
      res => {
        this.setState({
          selectedItems: [],
          isSelectAllChecked: false,
        });

        this.setItems();
      }
    );
  };

  onEditCustomerPermissionClick = () => {
    this.setState({
      isCustomerPermissionFormModalOpen: true,
    });
  };

  onCreateCustomerPermissionClick = () => {
    this.setState({
      isCustomerPermissionFormModalOpen: true,
    });
  };

  onCancelButtonClick = () => {
    this.setState({
      isCustomerPermissionFormModalOpen: false,
    });
  };

  onSubmitForm = data => {
    const { selectedItems, total } = this.state;
    const { user } = this.props;

    const customerId = this.getCustomerId();

    this.setState({
      isCustomerPermissionFormModalOpen: false,
      isLoading: true,
    });

    if (selectedItems.length === 1) {
      const [customer] = selectedItems;
      const { id } = customer;

      updateCustomerPermission(user, customerId, id, data)
        .then(res => {
          this.setState({
            selectedItems: [res],
            isSelectAllChecked: false,
          });
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          this.setState({
            isLoading: false,
            isErrorModalOpen: true,
            errorMessage: errorText,
          });
          console.error('error');
          console.error(errorText);
        });
    } else {
      addCustomerPermission(user, customerId, data)
        .then(res => {
          const lastPage = getPagesCount(total + 1, LIMIT);
          this.setState({
            selectedItems: [res],
            isSelectAllChecked: false,
          });
          this.setCurrentPageParams(lastPage);
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          this.setState({
            isLoading: false,
            isErrorModalOpen: true,
            errorMessage: errorText,
          });
          console.error('error');
          console.error(errorText);
        });
    }
  };

  renderFormModal(): React.ReactNode {
    const { isCustomerPermissionFormModalOpen, selectedItems } = this.state;
    const { user } = this.props;

    if (selectedItems.length === 1) {
      const customerPermission = selectedItems[0];
      return (
        <SimpleModal isOpen={isCustomerPermissionFormModalOpen}>
          <CustomerPermissionForm
            onCancel={this.onCancelButtonClick}
            onSubmit={this.onSubmitForm}
            customerPermission={customerPermission}
          />
        </SimpleModal>
      );
    } else {
      return (
        <SimpleModal isOpen={isCustomerPermissionFormModalOpen}>
          <CustomerPermissionForm onCancel={this.onCancelButtonClick} onSubmit={this.onSubmitForm} />
        </SimpleModal>
      );
    }
  }

  renderGrid(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      customer,
    } = this.state;

    const actionItems = [
      {
        itemText: 'Create customer permission',
        onItemClick: this.onCreateCustomerPermissionClick,
        isActive: selectedItems.length === 0,
      },
      {
        itemText: 'Edit customer permission',
        onItemClick: this.onEditCustomerPermissionClick,
        isActive: selectedItems.length === 1,
      },
      {
        itemText: selectedItems.length === 1 ? 'Delete customer permission' : 'Delete customer permissions',
        onItemClick: this.showConfirmationModal,
        isActive: selectedItems.length > 0,
      },
    ];

    const filterOptions = {};

    const gridTitle =
      typeof customer !== 'undefined' ? `Customer ${customer.firstName} ${customer.lastName} / Permissions` : '';

    return (
      <Grid
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={gridTitle}
        goBack={this.goBack}
      />
    );
  }

  render() {
    const { isLoading, isConfirmationModalOpen, isCustomerPermissionFormModalOpen, isErrorModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isConfirmationModalOpen || isCustomerPermissionFormModalOpen || isErrorModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderSendingError()}
        {this.renderConfirmationModal()}
        {this.renderFormModal()}
        <div className="row">
          <div className="col-md-12">{this.renderGrid()}</div>
        </div>
      </div>
    );
  }
}
